import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  getLocalizedPath,
  getRouteByKey,
  isRouteAvailable,
  normalizeSiteLanguage,
} from "@/utils/routing";

interface HomeAudienceItem {
  title: string;
  text: string;
  route: string;
}

function audienceHref(route: string, lang: ReturnType<typeof normalizeSiteLanguage>): string {
  const hashAt = route.indexOf("#");
  const hash = hashAt >= 0 ? route.slice(hashAt) : "";
  const routeKey = hashAt >= 0 ? route.slice(0, hashAt) : route;
  if (routeKey.startsWith("module:")) {
    const slug = routeKey.slice("module:".length);
    return getLocalizedPath(`/moduly/${slug}/`, lang) + hash;
  }
  if (!isRouteAvailable(routeKey, lang)) {
    return getRouteByKey("modules", lang);
  }
  return getRouteByKey(routeKey, lang) + hash;
}

export const HomeAudiences = () => {
  const { t, i18n } = useTranslation();
  const lang = normalizeSiteLanguage(i18n.resolvedLanguage || i18n.language);
  const items = t("pages.home.audiences.items", { returnObjects: true }) as HomeAudienceItem[];
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <div className="space-y-2 text-left" data-testid="home-audiences">
      <p className="text-sm font-semibold text-foreground">{t("pages.home.audiences.title")}</p>
      <p className="text-xs sm:text-sm text-muted-foreground">{t("pages.home.audiences.subtitle")}</p>
      <ul className="grid gap-2 sm:grid-cols-3">
        {items.map((item) => (
          <li key={item.route}>
            <Link
              to={audienceHref(item.route, lang)}
              className="block h-full rounded-lg border border-border bg-background p-3 shadow-sm hover:border-primary/40"
              data-testid={`home-audience-${item.route.replace(/[:#]/g, "-")}`}
            >
              <span className="block text-sm font-semibold text-foreground">{item.title}</span>
              <span className="mt-1 block text-xs text-muted-foreground">{item.text}</span>
            </Link>
          </li>
        ))}
      </ul>
      {isRouteAvailable("aiAssistant", lang) && (
        <Link
          to={getRouteByKey("aiAssistant", lang)}
          className="inline-flex text-sm font-medium text-primary hover:underline"
          data-testid="home-ai-bridge"
        >
          {t("pages.home.aiBridge")}
        </Link>
      )}
    </div>
  );
};
